import { FC } from "react";
import { format, getDaysInMonth, isSameDay, max } from "date-fns";
import { ConnectProto } from "@racoon/api";
import {
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@racoon/ui";
import { BrandIcMinus, BrandIcPlus } from "@racoon/icons";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { parseLocalDateParam } from "@racoon/lib";
import {
  DEFAULT_WINDOW,
  setWeekdayWeeks,
  toggleWeek,
  weekdayWeeks,
  weekMode,
  type WeekMode,
} from "../lib/schedule-form";
import { weekdayName, WEEKDAYS } from "../lib/schedule-label";
import type { ScheduleForm, WindowForm } from "../model/types";
import { MonthGrid } from "./month-grid";
import { WindowFields } from "./window-fields";

type Props = {
  form: ScheduleForm;
  onChange: (patch: Partial<ScheduleForm>) => void;
  preview?: ConnectProto.PreviewScheduleResponse;
  /** The schedule card: the same view, nothing to change. */
  readOnly?: boolean;
};

const WEEK_MODES: WeekMode[] = ["none", "every", "weeks"];
const WEEKS = [1, 2, 3, 4, 5];

const Weekly: FC<Props> = ({ form, onChange, readOnly }) => {
  const t = useT();
  const locale = useDateFnsLocale();
  const modeLabel = (dow: number, mode: WeekMode) =>
    readOnly && mode === "weeks"
      ? t(Namespace.PickupLocation, "schedules.rule.weeks", {
          weeks: weekdayWeeks(form, dow).join(", "),
        })
      : t(Namespace.PickupLocation, `schedules.editor.week.${mode}`);
  const setMode = (dow: number, mode: WeekMode) =>
    onChange({
      weekly: setWeekdayWeeks(form, dow, mode === "none" ? undefined : mode === "every" ? [] : [1]),
    });
  const setWindow = (dow: number, window: WindowForm) =>
    onChange({ weekly: form.weekly.map((d) => (d.dow === dow ? { ...d, windows: [window] } : d)) });

  return (
    <div className="flex w-full overflow-hidden rounded-md border border-border-primary bg-white">
      {WEEKDAYS.map((dow, i) => {
        const day = form.weekly.find((d) => d.dow === dow);
        const mode = weekMode(form, dow);
        return (
          <div
            key={dow}
            className={cn("flex min-w-0 flex-1 flex-col", i % 2 === 0 && "bg-surface-muted")}
          >
            <div
              className={cn(
                "flex h-12 items-center justify-center border-b-[0.5px] border-border-primary px-2 text-sm font-bold uppercase",
                day ? "text-text-information" : "text-text-disabled",
              )}
            >
              {weekdayName(dow, locale, "EEEE")}
            </div>
            <div className="flex min-h-16 flex-col justify-center gap-1 border-b border-border-primary p-2">
              {readOnly ? (
                <span className="w-full text-center text-sm text-text-body">
                  {modeLabel(dow, mode)}
                </span>
              ) : (
                <Select value={mode} onValueChange={(next) => setMode(dow, next as WeekMode)}>
                  <SelectTrigger
                    aria-label={weekdayName(dow, locale, "EEEE")}
                    className="!h-8 w-full !px-2 text-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEK_MODES.map((m) => (
                      <SelectItem key={m} value={m}>
                        {modeLabel(dow, m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!readOnly && mode === "weeks" && (
                <div className="flex justify-between gap-0.5">
                  {WEEKS.map((week) => (
                    <button
                      key={week}
                      type="button"
                      aria-pressed={day?.weeks.includes(week)}
                      aria-label={t(Namespace.PickupLocation, "schedules.editor.week.nth", {
                        n: week,
                      })}
                      onClick={() => onChange({ weekly: toggleWeek(form, dow, week) })}
                      className={cn(
                        "size-6 rounded text-xs font-medium",
                        day?.weeks.includes(week)
                          ? "bg-surface-action text-white"
                          : "border border-border-primary text-text-body",
                      )}
                    >
                      {week}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex h-16 items-center border-b border-border-primary px-2">
              {day && (
                <WindowFields
                  window={day.windows[0] ?? DEFAULT_WINDOW}
                  readOnly={readOnly}
                  onChange={(window) => setWindow(dow, window)}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Monthly: FC<Props> = ({ form, onChange, readOnly }) => (
  <MonthGrid
    count={31}
    selected={(day) => form.monthly.some((d) => d.day === day)}
    onToggle={
      readOnly
        ? undefined
        : (day) =>
            onChange({
              monthly: form.monthly.some((d) => d.day === day)
                ? form.monthly.filter((d) => d.day !== day)
                : [...form.monthly, { day, windows: [{ ...DEFAULT_WINDOW }] }].sort(
                    (a, b) => a.day - b.day,
                  ),
            })
    }
    renderSelected={(day) => {
      const entry = form.monthly.find((d) => d.day === day);
      return (
        <WindowFields
          window={entry?.windows[0] ?? DEFAULT_WINDOW}
          readOnly={readOnly}
          dashClassName="text-text-information"
          onChange={(window) =>
            onChange({
              monthly: form.monthly.map((d) => (d.day === day ? { ...d, windows: [window] } : d)),
            })
          }
        />
      );
    }}
  />
);

const EveryNDays: FC<Props> = ({ form, onChange, preview, readOnly }) => {
  const t = useT();
  const locale = useDateFnsLocale();
  const { n, windows } = form.everyNDays;
  const setN = (next: number) => onChange({ everyNDays: { ...form.everyNDays, n: next } });

  // PreviewSchedule starts at today or the schedule's start, whichever is later.
  const today = new Date();
  const validFrom = parseLocalDateParam(form.validFrom);
  const start = validFrom ? max([today, validFrom]) : today;
  const month = format(start, "yyyy-MM");
  const dates = new Set(preview?.occurrences.map((o) => o.date));
  const labelClass = "text-sm font-medium text-text-body";

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="flex items-center gap-12">
        <div className="flex items-center">
          <span className={cn(labelClass, "w-40")}>
            {t(Namespace.PickupLocation, "schedules.editor.repeatEvery")}
          </span>
          {readOnly ? (
            <span className={labelClass}>
              {t(Namespace.PickupLocation, "schedules.card.days", { n })}
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="−1"
                disabled={n <= 1}
                onClick={() => setN(n - 1)}
                className="size-8 text-icon-action disabled:opacity-40"
              >
                <BrandIcMinus className="size-full" />
              </button>
              <Input
                size="sm"
                inputMode="numeric"
                aria-label={t(Namespace.PickupLocation, "schedules.editor.repeatEvery")}
                // Cleared while typing a new number; 0 is refused on save (nInvalid)
                value={n ? String(n) : ""}
                onChange={(e) => setN(Number(e.target.value.replace(/\D/g, "")))}
                className="w-12 border-border-primary text-center !text-sm text-text-body"
              />
              <button
                type="button"
                aria-label="+1"
                onClick={() => setN(n + 1)}
                className="size-8 text-icon-action"
              >
                <BrandIcPlus className="size-full" />
              </button>
            </div>
          )}
        </div>
        <div className="flex w-[338px] items-center gap-6 p-2">
          <span className={labelClass}>
            {t(Namespace.PickupLocation, "schedules.editor.window")}
          </span>
          <WindowFields
            window={windows[0] ?? DEFAULT_WINDOW}
            readOnly={readOnly}
            onChange={(window) => onChange({ everyNDays: { n, windows: [window] } })}
          />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <span className="text-xs font-bold uppercase text-text-body">
          {t(Namespace.PickupLocation, "schedules.editor.previewFrom", {
            date: format(start, "d MMMM yyyy", { locale }),
          })}
          {isSameDay(start, today) && ` ${t(Namespace.PickupLocation, "schedules.editor.today")}`}
        </span>
        <MonthGrid
          count={getDaysInMonth(start)}
          selected={(day) => dates.has(`${month}-${String(day).padStart(2, "0")}`)}
        />
      </div>
    </div>
  );
};

/** The rule of the picked kind (Figma 22793:51108: weekly, monthly, every N days). */
export const RulesEditor: FC<Props> = (props) => {
  switch (props.form.kind) {
    case "weekly":
      return <Weekly {...props} />;
    case "monthly":
      return <Monthly {...props} />;
    case "everyNDays":
      return <EveryNDays {...props} />;
  }
};
